import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, Trash2, Edit3, Loader2, Lock, Save, UtensilsCrossed,
  Tag, Sliders, Package, Copy, XCircle, CheckCircle2, GripVertical,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const ALLERGEN_TAGS = ["milk", "eggs", "fish", "shellfish", "tree_nuts", "peanuts", "wheat", "soy", "sesame"];

const emptyGroup = () => ({
  name: "",
  description: "",
  selection: "single",
  required: false,
  min_select: 0,
  max_select: 1,
  position: 0,
  is_available: true,
  options: [{ name: "Option 1", price_adjust_cents: 0, is_default: false, is_available: true, max_qty: 1, position: 0 }],
});

export default function VendorMenuBuilder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const shopId = useMemo(() => (user?.user_id ? `shop_${user.user_id}` : null), [user]);

  const [tab, setTab] = useState("categories"); // categories | ingredients | modifiers
  const [categories, setCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupDraft, setGroupDraft] = useState(emptyGroup());
  const [editingGroupId, setEditingGroupId] = useState(null);

  const load = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const [cRes, iRes, gRes] = await Promise.all([
        api.get(`/shops/${shopId}/menu/categories/all`).catch(() => ({ data: { items: [] } })),
        api.get(`/shops/${shopId}/menu/ingredients`).catch(() => ({ data: { items: [] } })),
        api.get(`/shops/${shopId}/menu/modifier-groups`).catch(() => ({ data: { items: [] } })),
      ]);
      setCategories(cRes.data.items || []);
      setIngredients(iRes.data.items || []);
      setGroups(gRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load menu builder");
    } finally { setLoading(false); }
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  if (user && !user.is_vendor) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-3" data-testid="menu-not-vendor">
        <Lock className="w-10 h-10 text-zinc-500 mx-auto" />
        <h1 className="text-3xl font-black tracking-tighter">Vendors only</h1>
        <Button onClick={() => navigate("/sell")} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-11 px-5">Open Sell</Button>
      </div>
    );
  }

  // --- Categories ---
  const addCategory = async () => {
    const name = prompt("Category name (e.g. Burgers, Combos, Sides)");
    if (!name?.trim()) return;
    try {
      await api.post(`/shops/${shopId}/menu/categories`, { name: name.trim(), position: categories.length, is_active: true, daypart_days: [] });
      toast.success("Category created.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create"); }
  };
  const renameCategory = async (c) => {
    const name = prompt("New name", c.name);
    if (!name?.trim() || name === c.name) return;
    try {
      await api.put(`/shops/${shopId}/menu/categories/${c.category_id}`, {
        name: name.trim(), position: c.position, is_active: c.is_active, daypart_days: c.daypart_days || [],
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't rename"); }
  };
  const toggleCategory = async (c) => {
    try {
      await api.put(`/shops/${shopId}/menu/categories/${c.category_id}`, {
        name: c.name, position: c.position, is_active: !c.is_active, daypart_days: c.daypart_days || [],
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };
  const deleteCategory = async (c) => {
    if (!window.confirm(`Archive "${c.name}"? Products assigned to it will become uncategorized.`)) return;
    try {
      await api.delete(`/shops/${shopId}/menu/categories/${c.category_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't archive"); }
  };

  // --- Ingredients ---
  const addIngredient = async () => {
    const name = prompt("Ingredient name (e.g. Pickles, Beef patty)");
    if (!name?.trim()) return;
    try {
      await api.post(`/shops/${shopId}/menu/ingredients`, {
        name: name.trim(), allergen_tags: [], is_default_included: true, is_optional: true,
        is_removable: true, allow_light: true, allow_extra: true,
        extra_price_cents: 0, max_amount: 2, is_available: true,
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create"); }
  };
  const toggleIngExtra = async (ing, patch) => {
    try {
      await api.put(`/shops/${shopId}/menu/ingredients/${ing.ingredient_id}`, { ...ing, ...patch });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };
  const deleteIngredient = async (ing) => {
    if (!window.confirm(`Archive "${ing.name}"? Products using it will stop referencing it.`)) return;
    try {
      await api.delete(`/shops/${shopId}/menu/ingredients/${ing.ingredient_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't archive"); }
  };
  const setExtraPrice = async (ing) => {
    const s = prompt(`Extra price in cents for ${ing.name}`, ing.extra_price_cents);
    if (s === null) return;
    const v = Math.max(0, Math.min(5000, Number(s) || 0));
    try {
      await api.put(`/shops/${shopId}/menu/ingredients/${ing.ingredient_id}`, { ...ing, extra_price_cents: v });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };

  // --- Modifier groups ---
  const openGroupForm = (g) => {
    if (g) {
      setEditingGroupId(g.group_id);
      setGroupDraft({
        name: g.name, description: g.description || "", selection: g.selection, required: g.required,
        min_select: g.min_select, max_select: g.max_select, position: g.position, is_available: g.is_available,
        options: (g.options || []).map((o) => ({ ...o })),
      });
    } else {
      setEditingGroupId(null);
      setGroupDraft(emptyGroup());
    }
    setShowGroupForm(true);
  };
  const saveGroup = async () => {
    if (!groupDraft.name.trim()) return toast.error("Give the group a name.");
    if (!(groupDraft.options || []).length) return toast.error("Add at least one option.");
    for (const o of groupDraft.options) if (!o.name?.trim()) return toast.error("Options need names.");
    try {
      if (editingGroupId) {
        await api.put(`/shops/${shopId}/menu/modifier-groups/${editingGroupId}`, groupDraft);
      } else {
        await api.post(`/shops/${shopId}/menu/modifier-groups`, groupDraft);
      }
      toast.success(editingGroupId ? "Group updated." : "Group created.");
      setShowGroupForm(false);
      setEditingGroupId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };
  const dupGroup = async (g) => {
    try {
      await api.post(`/shops/${shopId}/menu/modifier-groups/${g.group_id}/duplicate`);
      toast.success("Duplicated.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't duplicate"); }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(`Archive "${g.name}"? Products using it will stop referencing it.`)) return;
    try {
      await api.delete(`/shops/${shopId}/menu/modifier-groups/${g.group_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't archive"); }
  };

  const updateOption = (i, patch) => setGroupDraft((g) => {
    const options = [...g.options];
    options[i] = { ...options[i], ...patch };
    return { ...g, options };
  });
  const addOption = () => setGroupDraft((g) => ({ ...g, options: [...g.options, { name: `Option ${g.options.length + 1}`, price_adjust_cents: 0, is_default: false, is_available: true, max_qty: 1, position: g.options.length }] }));
  const removeOption = (i) => setGroupDraft((g) => ({ ...g, options: g.options.filter((_, j) => j !== i) }));

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24 space-y-4" data-testid="menu-builder-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="menu-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="rounded-3xl border border-pink-400/30 bg-gradient-to-br from-pink-500/10 via-zinc-950 to-amber-500/10 p-5">
        <p className="inline-flex items-center gap-2 text-pink-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <UtensilsCrossed className="w-3 h-3" /> Menu Builder 3.0
        </p>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Build your menu</h1>
        <p className="text-sm text-zinc-400 mt-1">Categories, ingredients, and modifier groups. Everything you create here becomes available to your products.</p>
      </header>

      <div className="flex gap-2 border-b border-white/10">
        {[
          { k: "categories", label: "Categories", Icon: Tag },
          { k: "ingredients", label: "Ingredients", Icon: Package },
          { k: "modifiers", label: "Modifier groups", Icon: Sliders },
        ].map(({ k, label, Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`h-10 px-4 inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest ${tab === k ? "text-pink-300 border-b-2 border-pink-400" : "text-zinc-400 hover:text-white"}`}
            data-testid={`menu-tab-${k}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-zinc-500 text-sm inline-flex items-center gap-2 justify-center w-full"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : tab === "categories" ? (
        <section data-testid="menu-categories">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-zinc-400">{categories.length} categor{categories.length === 1 ? "y" : "ies"}</p>
            <Button onClick={addCategory} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-10 px-4 font-bold" data-testid="menu-category-add">
              <Plus className="w-4 h-4 mr-1" /> New category
            </Button>
          </div>
          {categories.length === 0 ? (
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500 text-sm" data-testid="menu-categories-empty">
              Create categories like Burgers, Combos, Sides, or Drinks to organize your menu.
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map((c) => (
                <div key={c.category_id} className={`rounded-2xl border p-3 flex items-center gap-3 ${c.is_active ? "border-white/10 bg-white/[0.02]" : "border-white/10 bg-zinc-900 opacity-60"}`} data-testid={`menu-category-${c.category_id}`}>
                  <GripVertical className="w-4 h-4 text-zinc-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black truncate">{c.name}</p>
                    <p className="text-[11px] text-zinc-500">Position {c.position} · slug {c.slug}{!c.is_active && " · hidden"}</p>
                  </div>
                  <button onClick={() => toggleCategory(c)} className="h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold" data-testid={`menu-category-toggle-${c.category_id}`}>
                    {c.is_active ? "Hide" : "Show"}
                  </button>
                  <button onClick={() => renameCategory(c)} className="h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold" data-testid={`menu-category-rename-${c.category_id}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deleteCategory(c)} className="h-8 px-3 rounded-full bg-rose-500/15 hover:bg-rose-500/25 border border-rose-400/40 text-rose-100 text-xs font-bold" data-testid={`menu-category-delete-${c.category_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : tab === "ingredients" ? (
        <section data-testid="menu-ingredients">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-zinc-400">{ingredients.length} ingredient{ingredients.length === 1 ? "" : "s"}</p>
            <Button onClick={addIngredient} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-10 px-4 font-bold" data-testid="menu-ingredient-add">
              <Plus className="w-4 h-4 mr-1" /> New ingredient
            </Button>
          </div>
          {ingredients.length === 0 ? (
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500 text-sm" data-testid="menu-ingredients-empty">
              Create ingredients once, then reference them across your products.
            </div>
          ) : (
            <div className="space-y-2">
              {ingredients.map((ing) => (
                <div key={ing.ingredient_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3" data-testid={`menu-ingredient-${ing.ingredient_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-black">{ing.name}</p>
                    {!ing.is_available && <span className="text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-400/40 text-rose-100">Unavailable</span>}
                    <button onClick={() => toggleIngExtra(ing, { is_available: !ing.is_available })} className="ml-auto h-7 px-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] font-bold" data-testid={`menu-ingredient-availability-${ing.ingredient_id}`}>
                      {ing.is_available ? "Mark sold out" : "Restore"}
                    </button>
                    <button onClick={() => deleteIngredient(ing)} className="h-7 px-2 rounded-full bg-rose-500/15 border border-rose-400/40 text-rose-100 text-[11px] font-bold" data-testid={`menu-ingredient-delete-${ing.ingredient_id}`}>Archive</button>
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
                    {[
                      ["Default", "is_default_included"],
                      ["Optional", "is_optional"],
                      ["Removable", "is_removable"],
                      ["Light allowed", "allow_light"],
                      ["Extra allowed", "allow_extra"],
                    ].map(([lbl, k]) => (
                      <label key={k} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 border border-white/10 cursor-pointer">
                        <input type="checkbox" checked={!!ing[k]} onChange={(e) => toggleIngExtra(ing, { [k]: e.target.checked })} className="accent-pink-500" data-testid={`menu-ingredient-${k}-${ing.ingredient_id}`} />
                        {lbl}
                      </label>
                    ))}
                    <button onClick={() => setExtraPrice(ing)} className="h-7 px-2 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-100 font-bold" data-testid={`menu-ingredient-price-${ing.ingredient_id}`}>
                      Extra: +${(ing.extra_price_cents / 100).toFixed(2)}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : (
        <section data-testid="menu-modifiers">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-zinc-400">{groups.length} modifier group{groups.length === 1 ? "" : "s"}</p>
            <Button onClick={() => openGroupForm(null)} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-10 px-4 font-bold" data-testid="menu-group-add">
              <Plus className="w-4 h-4 mr-1" /> New group
            </Button>
          </div>
          {groups.length === 0 ? (
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500 text-sm" data-testid="menu-groups-empty">
              Build reusable groups like <b>Choose Your Cheese</b>, <b>Pick Your Sauce</b>, or <b>Build Your Burger</b>. Then attach them to any product.
            </div>
          ) : (
            <div className="space-y-2">
              {groups.map((g) => (
                <div key={g.group_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={`menu-group-${g.group_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black truncate">{g.name}</p>
                      <p className="text-[11px] text-zinc-400">{g.instruction} · {(g.options || []).length} option{g.options?.length === 1 ? "" : "s"}</p>
                      {g.description && <p className="text-xs text-zinc-300 mt-1">{g.description}</p>}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(g.options || []).slice(0, 8).map((o) => (
                          <span key={o.option_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold">
                            {o.name}{o.price_adjust_cents ? ` ${o.price_adjust_cents > 0 ? "+" : ""}$${(o.price_adjust_cents / 100).toFixed(2)}` : ""}
                          </span>
                        ))}
                        {(g.options || []).length > 8 && <span className="text-[10px] text-zinc-500">+{g.options.length - 8} more</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openGroupForm(g)} className="h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold" data-testid={`menu-group-edit-${g.group_id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => dupGroup(g)} className="h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold" data-testid={`menu-group-dup-${g.group_id}`}><Copy className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteGroup(g)} className="h-8 px-3 rounded-full bg-rose-500/15 border border-rose-400/40 text-rose-100 text-xs font-bold" data-testid={`menu-group-delete-${g.group_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Group form modal */}
          {showGroupForm && (
            <div className="fixed inset-0 z-[95] grid place-items-center bg-black/85 backdrop-blur-sm p-4" onClick={() => setShowGroupForm(false)} data-testid="menu-group-modal">
              <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-950 rounded-3xl border border-pink-400/50" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-white/10 flex items-center justify-between">
                  <h3 className="text-lg font-black tracking-tight">{editingGroupId ? "Edit group" : "New modifier group"}</h3>
                  <button onClick={() => setShowGroupForm(false)} className="w-8 h-8 rounded-full grid place-items-center bg-white/5 hover:bg-white/10"><XCircle className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Group name</Label>
                    <Input value={groupDraft.name} onChange={(e) => setGroupDraft({ ...groupDraft, name: e.target.value })} placeholder="Choose Your Cheese" className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="menu-group-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Instructions (optional)</Label>
                    <Textarea value={groupDraft.description} onChange={(e) => setGroupDraft({ ...groupDraft, description: e.target.value })} maxLength={240} className="bg-white/5 border-white/10 rounded-2xl text-white" data-testid="menu-group-description" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Selection</Label>
                      <select value={groupDraft.selection} onChange={(e) => setGroupDraft({ ...groupDraft, selection: e.target.value, max_select: e.target.value === "single" ? 1 : groupDraft.max_select })} className="h-11 w-full bg-white/5 border border-white/10 rounded-2xl text-white px-3" data-testid="menu-group-selection">
                        <option value="single">Single — pick 1</option>
                        <option value="multi">Multi — pick several</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Required?</Label>
                      <label className={`h-11 px-4 rounded-2xl border flex items-center gap-2 cursor-pointer ${groupDraft.required ? "bg-pink-500/10 border-pink-400/40" : "bg-white/5 border-white/10"}`}>
                        <input type="checkbox" checked={groupDraft.required} onChange={(e) => setGroupDraft({ ...groupDraft, required: e.target.checked })} className="accent-pink-500" data-testid="menu-group-required" />
                        <span className="text-sm">Required</span>
                      </label>
                    </div>
                    {groupDraft.selection === "multi" && (
                      <>
                        <div className="space-y-1.5">
                          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Min select</Label>
                          <Input type="number" min="0" value={groupDraft.min_select} onChange={(e) => setGroupDraft({ ...groupDraft, min_select: Number(e.target.value) || 0 })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="menu-group-min" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Max select</Label>
                          <Input type="number" min="1" value={groupDraft.max_select} onChange={(e) => setGroupDraft({ ...groupDraft, max_select: Number(e.target.value) || 1 })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="menu-group-max" />
                        </div>
                      </>
                    )}
                  </div>
                  <div>
                    <p className="text-zinc-300 text-xs uppercase tracking-widest font-bold mb-2">Options</p>
                    <div className="space-y-2">
                      {groupDraft.options.map((o, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`menu-group-option-${i}`}>
                          <Input value={o.name} onChange={(e) => updateOption(i, { name: e.target.value })} placeholder="Option name" className="h-10 bg-white/5 border-white/10 rounded-xl text-white text-sm col-span-5" />
                          <Input type="number" value={o.price_adjust_cents} onChange={(e) => updateOption(i, { price_adjust_cents: Number(e.target.value) || 0 })} placeholder="¢ delta" className="h-10 bg-white/5 border-white/10 rounded-xl text-white text-sm col-span-3" />
                          <label className="col-span-2 inline-flex items-center gap-1 text-xs text-zinc-300">
                            <input type="checkbox" checked={!!o.is_default} onChange={(e) => updateOption(i, { is_default: e.target.checked })} className="accent-pink-500" />
                            Default
                          </label>
                          <label className="col-span-1 inline-flex items-center gap-1 text-xs text-zinc-300">
                            <input type="checkbox" checked={!!o.is_available} onChange={(e) => updateOption(i, { is_available: e.target.checked })} className="accent-pink-500" />
                            Ok
                          </label>
                          <button onClick={() => removeOption(i)} className="col-span-1 h-10 rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-100 grid place-items-center">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button onClick={addOption} className="mt-2 h-9 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold inline-flex items-center gap-1" data-testid="menu-group-add-option">
                      <Plus className="w-3.5 h-3.5" /> Add option
                    </button>
                  </div>
                </div>
                <div className="p-4 border-t border-white/10 flex items-center gap-2 justify-end">
                  <button onClick={() => setShowGroupForm(false)} className="h-10 px-4 rounded-full bg-white/5 hover:bg-white/10 text-sm font-bold" data-testid="menu-group-cancel">Cancel</button>
                  <Button onClick={saveGroup} className="h-10 px-5 rounded-full bg-pink-500 hover:bg-pink-400 text-white text-sm font-bold" data-testid="menu-group-save">
                    <Save className="w-4 h-4 mr-1.5" /> {editingGroupId ? "Save changes" : "Create group"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
